import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

function Login() {
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // Check for pre-filled mobile from multi-station login
  useEffect(() => {
    const prefillMobile = sessionStorage.getItem('prefill_mobile');
    if (prefillMobile) {
      setMobile(prefillMobile);
      sessionStorage.removeItem('prefill_mobile');
      toast.info(`Please enter PIN for ${prefillMobile}`);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/login`, { mobile, pin });
      login(res.data.token, res.data.user);
      
      if (res.data.user.is_first_login) {
        navigate('/reset-pin');
      } else if (res.data.requires_subscription) {
        navigate('/payments');
        toast.info('Please subscribe to continue using Fuelogic');
      } else {
        navigate('/');
      }
      toast.success('Login successful');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center lg:justify-end"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ec6a3507-f3ff-46ed-bf60-38c4e580efd9/artifacts/u116accu_RisingSunOverPumpWithGreenry.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Login Card - Positioned on the right */}
      <div className="w-full max-w-md mx-4 sm:mx-8 lg:mx-0 lg:mr-16 xl:mr-24 my-8 lg:ml-auto">
        <div 
          className="rounded-2xl p-6 sm:p-8 backdrop-blur-md"
          style={{
            backgroundColor: 'rgba(30, 30, 30, 0.25)',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)'
          }}
        >
          {/* Fuelogic Logo */}
          <div className="flex flex-col items-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_ec6a3507-f3ff-46ed-bf60-38c4e580efd9/artifacts/7bo5ekiy_ee42b44f-387e-4368-9e47-34931b0ff6a5-removebg-preview.png" 
              alt="Fuelogic" 
              className="w-40 h-40 object-contain" 
            />
          </div>

          {/* Login Heading */}
          <h2 
            className="text-2xl font-heading font-bold text-center mb-1 text-white"
            data-testid="login-heading"
          >
            Welcome Back
          </h2>
          <p className="text-gray-200 text-center text-sm mb-6">
            Sign in to your pump account
          </p>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-white mb-1.5">
                Mobile Number
              </label>
              <Input
                data-testid="login-mobile-input"
                type="tel"
                placeholder="Enter your mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                className="h-11 bg-white/90 border-gray-200 text-gray-900 placeholder:text-gray-400"
                style={{ fontSize: '15px' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1.5">
                PIN
              </label>
              <Input
                data-testid="login-pin-input"
                type="password"
                placeholder="Enter 6-digit PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                maxLength={6}
                required
                className="h-11 bg-white/90 border-gray-200 text-gray-900 placeholder:text-gray-400"
                style={{ fontSize: '15px' }}
              />
            </div>

            <Button 
              data-testid="login-submit-button"
              type="submit" 
              className="w-full h-11 text-base font-medium"
              style={{
                backgroundColor: '#2563EB',
                color: 'white'
              }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Setup Link */}
          <div className="mt-5 text-center">
            <p className="text-sm text-gray-200">
              Don't have an account?{' '}
              <a 
                href="/setup" 
                className="font-medium hover:underline text-cyan-300"
              >
                Setup Pump
              </a>
            </p>
          </div>

          {/* Admin Portal Link */}
          <div className="mt-4 pt-4 border-t border-white/20 text-center">
            <a 
              href="/master-admin/login" 
              className="text-xs text-gray-300 hover:text-white transition-colors"
            >
              Solution Provider Portal →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
